import type { ClientTenantOptions } from "@hooks/auth/useClientTenancyInputs";
import { Alert, Container, Stack } from "@repo/ui/core";
import { IconAlertTriangleFilled } from "@repo/ui/icons";
import { parseJWT } from "@repo/utils/lib/jwt.js";
import type { Maybe, Nullable } from "@type/helper";
import {
  WebStorageStateStore,
  type IdTokenClaims,
  type User,
} from "oidc-client-ts";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { AuthProvider as OidcAuthProvider, useAuth } from "react-oidc-context";
import { useAppContext } from "./AppContext";
import { STORAGE_KEY, useStorageContext } from "./StorageContext";

/**
 * Prefix for deriving clientId from realm for tenant-specific authentication
 */
const TENANT_CLIENT_PREFIX = "ctrl-";

/**
 * Extended OIDC token claims with Keycloak-specific extensions for RBAC
 */
interface ExtendedTokenClaims extends IdTokenClaims {
  resource_access?: Record<string, { roles?: string[] }>;
  realm_access?: { roles?: string[] };
}

/**
 * declare singleton type for oidc settings
 */
type OidcSettings = Nullable<Partial<ReturnType<typeof useAuth>["settings"]>>;

/**
 * Module-level storage singletons shared by every UserManager instance.
 * Recreating these per render is wasteful, and serializing them (they
 * expose the backing Storage object) leaks all of localStorage into
 * anything that stringifies the settings.
 */
const oidcUserStore = new WebStorageStateStore({ store: window.localStorage });
const oidcStateStore = new WebStorageStateStore({
  store: window.localStorage,
});

/**
 * Auth provider HOC to configure OIDC auth provider
 */
export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) =>
  import.meta.env.DEV && import.meta.env.NA_MOCK === "true" ? (
    <MockAuthProvider>{children}</MockAuthProvider>
  ) : (
    <OidcAuthProviderRoot>{children}</OidcAuthProviderRoot>
  );

/**
 * Dev-only signed-in session for MSW mock mode. No Keycloak round-trip.
 */
const MockAuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const value = useMemo<Context>(
    () => ({
      initialized: true,
      token: "mock-token",
      tokenData: {
        sub: "u-glen",
        name: "Glen Balliet",
        email: "glen.balliet@kestrel.dev",
        preferred_username: "gballiet",
      } as ExtendedTokenClaims,
      user: "gballiet",
      name: "Glen Balliet",
      initials: "GB",
      roles: [],
      tenantConfig: {
        tenantId: "t-kestrel",
        realm: "kestrel",
        clientId: "ctrl-kestrel",
      },
      isAuthenticated: () => true,
      handleUnauthorized: () => undefined,
      login: () => Promise.resolve(),
      logout: () => undefined,
      setClientTenantOptions: () => undefined,
      loginWithRealm: () => Promise.resolve(),
    }),
    [],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const OidcAuthProviderRoot: React.FC<PropsWithChildren> = ({ children }) => {
  const {
    env: { NA_AUTH_URL, NA_AUTH_REALM, NA_AUTH_CLIENT_ID },
  } = useAppContext();

  const { storage } = useStorageContext();

  const getStoredConfig = useCallback(
    () => storage.get<ClientTenantOptions>(STORAGE_KEY.TENANT),
    [storage],
  );

  // Resolve the stored realm synchronously so the OIDC provider mounts with
  // the correct authority on the very first render. Setting it in an effect
  // remounts the provider after mount — and when the URL still carries the
  // ?code&state callback params, two UserManager instances race to consume
  // the single-use auth code and signin state, leaving the UI flapping
  // between authenticated and unauthenticated.
  const [authRealm, setAuthRealm] = useState<Maybe<Nullable<string>>>(
    () => getStoredConfig()?.realm,
  );

  // Track pending login - survives OidcProvider remount
  const [pendingLogin, setPendingLogin] = useState(false);

  const defaultConfig = useMemo<ClientTenantOptions>(() => {
    return {
      tenantId: "",
      realm: NA_AUTH_REALM,
      clientId: NA_AUTH_CLIENT_ID,
    };
  }, [NA_AUTH_REALM, NA_AUTH_CLIENT_ID]);

  const tenantConfig = useMemo<ClientTenantOptions>(() => {
    const storedConfig = getStoredConfig();

    // If custom realm is set (different from default), use it
    if (authRealm && authRealm !== defaultConfig.realm) {
      return {
        realm: authRealm,
        // Use explicit clientId if stored (from onboarding), otherwise derive with prefix
        clientId:
          storedConfig?.clientId || `${TENANT_CLIENT_PREFIX}${authRealm}`,
        tenantId: storedConfig?.tenantId || "", // Preserve if exists
      };
    }

    // Otherwise use default config
    return defaultConfig;
  }, [defaultConfig, authRealm, getStoredConfig]);

  // map the specific configurations to settings on the oidc provider
  const oidcConfig = useMemo<OidcSettings>(() => {
    if (!NA_AUTH_URL) return null;

    const authority = `${NA_AUTH_URL}/realms/${tenantConfig.realm}`;

    return {
      authority,
      client_id: tenantConfig.clientId,
      redirect_uri: window.location.origin,
      post_logout_redirect_uri: window.location.origin,
      silent_redirect_uri: `${window.location.origin}/silent-sso.html`,
      scope: "openid profile email",
      automaticSilentRenew: true,
      loadUserInfo: true,
      // Keep the Keycloak check-session iframe off (library default). With
      // third-party cookies blocked the session probe fails spuriously and
      // raises userSignedOut, flipping the UI to unauthenticated while the
      // token is still valid. Token freshness is handled by silent renew.
      monitorSession: false,
      response_type: "code",
      filterProtocolClaims: true,
      userStore: oidcUserStore,
      stateStore: oidcStateStore,
    };
  }, [NA_AUTH_URL, tenantConfig.realm, tenantConfig.clientId]);

  // Identity key for the provider: remount only when the effective issuer or
  // client changes. (Do not JSON.stringify the settings — the state stores
  // serialize the entire contents of localStorage, so the key would change
  // whenever tokens are written.)
  const oidcConfigKey = useMemo(
    () => (oidcConfig ? `${oidcConfig.authority}|${oidcConfig.client_id}` : ""),
    [oidcConfig],
  );

  // With "Remember me" on, keep the email so the next sign-in can send it as
  // login_hint; Keycloak's organization step then skips the email prompt and
  // redirects straight to the IdP linked to that email's domain.
  const onSigninCallback = useCallback(
    (user?: User) => {
      const email = user?.profile.email;
      if (email && storage.get(STORAGE_KEY.REMEMBER_ME, false)) {
        storage.set(STORAGE_KEY.LOGIN_HINT, email);
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    },
    [storage],
  );

  if (!oidcConfig) {
    return (
      <Container>
        <Stack justify="center" h="100vh">
          <Alert
            my="auto"
            variant="outline"
            color="red"
            icon={<IconAlertTriangleFilled size={20} />}
          >
            Unable to resolve authentication configurations
          </Alert>
        </Stack>
      </Container>
    );
  }

  return (
    <OidcAuthProvider
      key={oidcConfigKey} // force remount when settings change
      onSigninCallback={onSigninCallback}
      {...oidcConfig}
    >
      <AuthContextProvider
        setAuthRealm={setAuthRealm}
        tenantConfig={tenantConfig}
        primaryRealm={NA_AUTH_REALM}
        primaryClientId={NA_AUTH_CLIENT_ID}
        pendingLogin={pendingLogin}
        setPendingLogin={setPendingLogin}
      >
        {children}
      </AuthContextProvider>
    </OidcAuthProvider>
  );
};

/**
 * Application auth context interface
 */
interface Context {
  initialized: boolean;
  token: Maybe<string>;
  tokenData: Maybe<ExtendedTokenClaims>;
  user: Maybe<string>;
  name: Maybe<string>;
  initials: Maybe<string>;
  roles: Maybe<string[]>;
  tenantConfig: ClientTenantOptions;
  isAuthenticated: () => boolean;
  /** ctrl rejected `token` with 401: renew the session or end it. */
  handleUnauthorized: (token: string) => void;
  login: () => Promise<void>;
  logout: () => void;
  setClientTenantOptions: (config: Partial<ClientTenantOptions>) => void;
  loginWithRealm: (realm: string, clientId?: string) => Promise<void>;
}

export const AuthContext = createContext<Context>({} as Context);
export function useAuthContext(): Context {
  return useContext(AuthContext) as Context;
}

/**
 * Internal auth context provider with OIDC integration
 */
export const AuthContextProvider: React.FC<
  PropsWithChildren<
    Pick<Context, "tenantConfig"> & {
      primaryRealm: string;
      primaryClientId: string;
      setAuthRealm: React.Dispatch<
        React.SetStateAction<string | undefined | null>
      >;
      pendingLogin: boolean;
      setPendingLogin: React.Dispatch<React.SetStateAction<boolean>>;
    }
  >
> = ({
  children,
  tenantConfig,
  primaryRealm,
  primaryClientId,
  setAuthRealm,
  pendingLogin,
  setPendingLogin,
}) => {
  const auth = useAuth();
  const { storage } = useStorageContext();

  const initialized = useMemo(
    () => !auth.isLoading && !auth.activeNavigator,
    [auth.isLoading, auth.activeNavigator],
  );

  const token = useMemo(
    () => auth.user?.access_token,
    [auth.user?.access_token],
  );

  const tokenData = useMemo<Maybe<ExtendedTokenClaims>>(() => {
    if (token) {
      return parseJWT<ExtendedTokenClaims>(token) ?? undefined;
    }
    return undefined;
  }, [token]);

  const isAuthenticated = useCallback(
    (): boolean => auth.isAuthenticated,
    [auth.isAuthenticated],
  );

  const user = useMemo(
    () => tokenData?.preferred_username ?? tokenData?.email ?? tokenData?.sub,
    [tokenData],
  );

  const name = useMemo(() => tokenData?.name, [tokenData]);

  const initials = useMemo(() => {
    const userName = tokenData?.name ?? "";
    const parts = userName.split(/\s/);
    const [first, last] = parts;
    return [first, last]
      .filter(Boolean)
      .map((str) => str?.at(0)?.toUpperCase())
      .join("");
  }, [tokenData]);

  const roles = useMemo(() => {
    const resourceRoles =
      tokenData?.resource_access?.[tenantConfig?.clientId]?.roles ?? [];
    const realmRoles = tokenData?.realm_access?.roles ?? [];
    return [...new Set([...resourceRoles, ...realmRoles])];
  }, [tokenData, tenantConfig]);

  const setClientTenantOptions = useCallback(
    (partialConfig: Partial<ClientTenantOptions>) => {
      const config: ClientTenantOptions = {
        realm: partialConfig.realm || primaryRealm,
        clientId:
          partialConfig.clientId ||
          (partialConfig.realm
            ? `${TENANT_CLIENT_PREFIX}${partialConfig.realm}`
            : primaryClientId),
        tenantId: partialConfig.tenantId || "",
      };
      setAuthRealm(config.realm);
      storage.set(STORAGE_KEY.TENANT, config);
    },
    [storage, primaryRealm, primaryClientId, setAuthRealm],
  );

  // A full-page redirect, not a popup: a popup can open behind the window or
  // be blocked, leaving the sign-in page waiting on a window nobody sees.
  // The return path survives the round-trip in session storage.
  const login = useCallback(
    () =>
      auth.signinRedirect({
        login_hint:
          storage.get<Maybe<string>>(STORAGE_KEY.LOGIN_HINT) || undefined,
      }),
    [auth, storage],
  );

  // Login with a specific realm - updates config first, then triggers login
  const loginWithRealm = useCallback(
    (realm: string, clientId?: string) => {
      // Derive clientId if not provided
      const effectiveClientId = clientId || `${TENANT_CLIENT_PREFIX}${realm}`;

      // Check if we need to update the config
      const needsUpdate =
        tenantConfig.realm !== realm ||
        tenantConfig.clientId !== effectiveClientId;

      if (needsUpdate) {
        // Update config and set pending login
        setClientTenantOptions({
          realm,
          clientId: effectiveClientId,
          tenantId: "",
        });
        setPendingLogin(true);
        return Promise.resolve();
      }
      // Config already correct, login immediately
      return login();
    },
    [tenantConfig, setClientTenantOptions, login, setPendingLogin],
  );

  // Trigger login after realm update completes
  useEffect(() => {
    if (pendingLogin && initialized && !auth.isLoading) {
      setPendingLogin(false);
      login().catch((e: unknown) =>
        console.error("Sign-in redirect failed:", e),
      );
    }
  }, [pendingLogin, initialized, auth.isLoading, login, setPendingLogin]);

  // isAuthenticated is only computed when a user loads, so a failed silent
  // renew (e.g. Keycloak session idled out while the laptop slept) leaves
  // the UI signed in with an expired token and every API call 401s. On
  // expiry retry once, then drop the user so ProtectedRoute sends them to
  // /login with the current path preserved.
  const { events, signinSilent, removeUser } = auth;
  useEffect(
    () =>
      events.addAccessTokenExpired(() => {
        signinSilent().catch(() => removeUser());
      }),
    [events, signinSilent, removeUser],
  );

  // The expiry timer above only knows the token's own exp. When ctrl answers
  // 401 the token is dead whatever the timer thinks, and pollers would keep
  // sending it for hours. Renew once; if that fails, or ctrl rejects the
  // token we just renewed, drop the user. A 401 for an older token is a
  // request that was in flight during a renewal, so it is ignored.
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  const renewing = useRef(false);
  const renewedToken = useRef<string | undefined>(undefined);
  const handleUnauthorized = useCallback(
    (rejected: string) => {
      if (renewing.current || rejected !== tokenRef.current) return;
      if (rejected === renewedToken.current) {
        void removeUser();
        return;
      }
      renewing.current = true;
      signinSilent()
        .then((renewed) => {
          if (!renewed) return removeUser();
          renewedToken.current = renewed.access_token;
          // Don't wait for the re-render: a 401 for the old token that
          // lands in between must count as stale, not trigger another renew.
          tokenRef.current = renewed.access_token;
        })
        .catch(() => removeUser())
        .finally(() => {
          renewing.current = false;
        });
    },
    [signinSilent, removeUser],
  );

  const logout = useCallback(() => {
    auth.signoutRedirect();
  }, [auth]);

  return (
    <AuthContext.Provider
      value={{
        initialized,
        token,
        tokenData,
        user,
        name,
        initials,
        roles,
        tenantConfig,
        isAuthenticated,
        handleUnauthorized,
        login,
        logout,
        setClientTenantOptions,
        loginWithRealm,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
